import { useCallback, useReducer } from 'react'
import { mergeAll } from 'ramda'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { isEmptyOrNil } from 'libs/empty'
import { useMutation } from '@tanstack/react-query'

import effects from 'app/effects/effects'
import { useAppName } from 'commons/usePartner'

import MessageAlert from 'app/components/MessageAlert'

export const ActionType = {
  SET_STEP: 'SET_STEP',
  SET_STEP_AND_VALUE: 'SET_STEP_AND_VALUE',
  SET_VALUES: 'SET_VALUES',
}

const initialState = {
  step: 1,
  form: {
    address: '',
    category: null,
    location: null,
    placeName: '',
    googlePlaceId: null,
    logo: '',
    originAI: '',
    typing: false,
    description: '',
  },
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_STEP_AND_VALUE:
      return {
        ...state,
        step: action.value,
        form: action.form,
      }
    case ActionType.SET_STEP:
      return {
        ...state,
        step: action.value,
        form: {
          ...state.form,
          typing: action.typing,
        },
      }
    case ActionType.SET_VALUES:
      return {
        ...state,
        form: mergeAll([state.form, action.values]),
      }
    default:
      return state
  }
}

export default function useOnboard() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const appName = useAppName()
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()

  const errors = useCallback(
    (text) => {
      const generalError = formatMessage({ id: 'error.InternalServerError' })
      return commonDispatch(
        MessageAlert.showError({
          message: text ?? generalError,
        }),
      )
    },
    [commonDispatch, formatMessage],
  )

  const handleAlertError = useCallback(
    ({ name }) => {
      errors(
        formatMessage(
          {
            id: `error.${name}`,
          },
          { newline: <br />, appName },
        ),
      )
    },
    [appName, errors, formatMessage],
  )

  const handleStep = useCallback((valueKey, typing = true) => {
    dispatch({
      type: 'SET_STEP',
      value: valueKey,
      typing: typing,
    })
  }, [])

  const handleForm = (values) => {
    dispatch({
      type: 'SET_VALUES',
      values: values,
    })
  }

  const descriptionAI = useMutation({
    mutationFn: async (value) => {
      const response = await effects.apis.onboarding.descriptionAI({
        text: value.placeName,
        category: value.category.name,
        address: value.address,
      })
      return response
    },
    onSuccess: (data, value) => {
      dispatch({
        type: 'SET_STEP_AND_VALUE',
        value: 2,
        form: {
          ...value,
          originAI: data.response ?? '',
          description: data.response ?? '',
          typing: !isEmptyOrNil(data.response),
        },
      })
    },
    onError: (error) => {
      const msgError = error.response.data
      errors(msgError.details.message)
    },
  })

  const checkGoogleCurated = useMutation({
    mutationFn: async (value) => {
      const response = await effects.apis.onboarding.checkGoogleCurated({
        googlePlaceId: value.googlePlaceId,
      })
      return response
    },
    onSuccess: ({ response: { existing } }, value) => {
      if (existing) return existing
      else descriptionAI.mutate(value)
    },
    onError: (error) => {
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  const handleStep2 = (values) => {
    if (values.googlePlaceId) checkGoogleCurated.mutate(values)
    else descriptionAI.mutate(values)
  }

  const setupPlace = useMutation({
    mutationFn: async (values) => {
      const comb = mergeAll([state.form, values])
      const response = await effects.apis.onboarding.setupPlace({
        googlePlaceId: comb.googlePlaceId,
        placeName: comb.placeName,
        description: comb.description,
        category: {
          id: comb.category.id,
          name: comb.category.name,
        },
        location: comb.address,
        lat: comb.location?.latitude || null,
        lng: comb.location?.longitude || null,
      })
      return response
    },
    onSuccess: () => {
      handleStep(3)
    },
    onError: (error) => {
      const msgError = error.response.data.error
      handleAlertError(msgError)
    },
  })

  return {
    state,
    handleStep,
    setupPlace,
    handleForm,
    handleStep2,
    descriptionAI,
    checkGoogleCurated,
  }
}
